import React from 'react';
import {Button, Col, Divider, Form, Row, Typography} from "antd";
import * as Yup from 'yup';
import {Formik} from "formik";
import {
  CheckboxInput,
  CodeMultiFastInput,
  CodeSingleFastInput,
  CodeTreeFastInput,
  DateTimeInput,
  DollarInput,
  TextAreaInput,
  TextInput,
  UserSelectInput,
} from "../Boilerplate/formikComponents";
import {cloneDeep} from "lodash";
import {getBidInvites$} from "../../services/bids";
import TooltipIcon from "../Boilerplate/TooltipIcon";
import * as R from "ramda";
import {nameSort} from "../../common/sorters";

const handleSearch = values => async (q, id) => {
  let soFar = 0;
  let theSize = 0;
  let nextOffset;
  let data = [];
  do {
    let { response, status } = await getBidInvites$({
      bidId: values['id'],
      q, id,
      offset: nextOffset,
    }).toPromise();
    if (status === 204 || response === null) return data;
    const { results, size } = response;
    theSize = size;
    soFar += results.length;
    if (results.length > 0) {
      data = data.concat(results);
      nextOffset = R.last(results).contactuserid;
    }
  } while (soFar < theSize);
  data.sort((a, b) => nameSort(a, b));
  return data;
};

const BidForm = props => {
  const { bid, onCancel, onSubmit } = props;
  let initials, edit = false;
  if (bid) {
    initials = cloneDeep(bid);
    edit = true;
  } else {
    initials = {
      status: 'open',
      prebidmeeting: false,
      prebidmeetingrequired: false,
    };
  }
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 },
    }
  };
  const tailFormItemLayout = {
    wrapperCol: {
      xs: {
        span: 24,
        offset: 0,
      },
      sm: {
        span: 16,
        offset: 8,
      },
    },
  };
  return (
    <Formik
      initialValues = {initials}
      onSubmit={(values, { setSubmitting }) => {
        values = cloneDeep(values);
        if (!values['prebidmeeting']) {
          values.prebidmeetingrequired = false;
          values.prebidmeetingdatetime = null;
          values.prebidmeetinglocation = null;
        }
        values.contractsize = values.contractsize ? typeof(values.contractsize) == 'string' ? values.contractsize.replace(/[^.0-9+-]/g, '') : values.contractsize : null;
        onSubmit && onSubmit(values);
      }}
      validationSchema={Yup.object().shape({
        id: Yup.string().nullable(),
        number: Yup.string(),
        name: Yup.string().required(),
        bidduedatetime: Yup.object().nullable(),
        status: Yup.string().required().default('open'),
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        city: Yup.string().nullable(),
        state: Yup.string().nullable(),
        zip: Yup.string().nullable(),
        prebidmeeting: Yup.boolean().default(false),
        prebidmeetingrequired: Yup.boolean().default(false),
        prebidmeetingdatetime: Yup.object().nullable(),
        prebidmeetinglocation: Yup.string().nullable(),
        architectname: Yup.string().nullable(),
        engineername: Yup.string().nullable(),
        siteownername: Yup.string().nullable(),
        contractsize: Yup.number().nullable(),
      })}
    >
      {props => {
        const {
          // eslint-disable-next-line
          values, touched, errors, isSubmitting, handleChange, handleBlur, dirty,
          handleSubmit,
          handleReset,
        } = props;
        return (
          <Form>
            <TextInput {...props} formItemLayout={formItemLayout} label='Title' name='name' placeholder='Enter a title'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Bid #' name='number' placeholder='Leave blank or enter your own'/>
            {!edit && <Row style={{ marginTop: -24, fontSize: 13 }}>
              <Col xs={24} sm={0} style={{ width: 400 }}>
                <Typography><Typography.Paragraph><Typography.Text type={'secondary'}>
                  cProject will auto generate this if left blank. The Bid # is intended to be used as a communication
                  reference, written or verbal. [Example: "John, has everyone been invited to Bid
                  #22?"]</Typography.Text></Typography.Paragraph></Typography>
              </Col>
              <Col xs={0} sm={16} offset={8} style={{ width: 400 }}>
                <Typography><Typography.Paragraph><Typography.Text type={'secondary'}>cProject will auto generate this
                  if left blank. The Bid # is intended to be used as a communication reference, written or verbal.
                  [Example: "John, has everyone been invited to Bid
                  #22?"]</Typography.Text></Typography.Paragraph></Typography>
              </Col>
            </Row>}
            <DollarInput {...props} formItemLayout={formItemLayout} label='Estimated Cost' name='contractsize' />
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Proposals Bid To' name='proposalsbidto' placeholder='Bid to...' autosize={{minRows: 1, maxRows: 5}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Bidders List' name='bidderslist' placeholder='Bidding GCs...' autosize={{minRows: 1, maxRows: 5}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Description' name='description' placeholder='Project description'/>
            {edit && <React.Fragment>
              <UserSelectInput {...props} formItemLayout={formItemLayout} label='Bid Lead' name='owneruserid' onSearch={handleSearch(values)}/>
              <UserSelectInput {...props} formItemLayout={formItemLayout} label='Bid Creator' name='createdbyuserid' onSearch={handleSearch(values)} disabled={true}/>
            </React.Fragment>}
            <CodeSingleFastInput {...props} disabled={!edit} formItemLayout={formItemLayout} label='Status' name='status' table='bidstatus'/>
            <Divider>Site Address</Divider>
            <TextInput {...props} formItemLayout={formItemLayout} label='Address Line 1' name='address' placeholder='Street address'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Address Line 2' name='address2' placeholder='Suite #'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='City' name='city' placeholder='City'/>
            <CodeSingleFastInput {...props} formItemLayout={formItemLayout} label='State' name='state' table='states' placeholder='Type a state name...' defaultValue='TX'/>
            <TextInput {...props} formItemLayout={formItemLayout} label='Zip' name='zip' placeholder='Zip Code'/>
            <Divider>Pre-Bid Meeting</Divider>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Pre-bid Meeting?' name='prebidmeeting'/>
            {values['prebidmeeting'] && <React.Fragment>
              <CheckboxInput {...props} formItemLayout={formItemLayout} label='Mandatory?' name='prebidmeetingrequired'/>
              <DateTimeInput {...props} pickerLayout={{ dropdownClassName: 'highlight-time-picker', defaultTime: '10:00:00' }} formItemLayout={formItemLayout} label='Meeting Time' name='prebidmeetingdatetime'/>
              <TextAreaInput {...props} formItemLayout={formItemLayout} label='Location' name='prebidmeetinglocation' autosize={{ minRows: 1, maxRows: 6 }}/>
            </React.Fragment>}
            <Divider>Additional Details</Divider>
            <DateTimeInput {...props} pickerLayout={{ dropdownClassName: 'highlight-time-picker' }} formItemLayout={formItemLayout} label='Bid Due Date/Time' name='bidduedatetime'/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Architect' name='architectname' autosize={{minRows: 1, maxRows: 6}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Engineer' name='engineername' autosize={{minRows: 1, maxRows: 6}}/>
            <TextAreaInput {...props} formItemLayout={formItemLayout} label='Site Owner' name='siteownername' autosize={{minRows: 1, maxRows: 6}}/>
            <Divider/>
            <CodeTreeFastInput {...props} formItemLayout={formItemLayout} label='CSI Codes' name='csicodes' table='csi_hierarchy'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Industries' name='industries' table='industry'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Sector' name='sectors' table='sector'/>
            <CodeMultiFastInput {...props} formItemLayout={formItemLayout} label='Set Asides' name='setasides' table='setaside'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='New Construction' name='newconstruction'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Union' name='unionized'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Certified Payroll' name='certifiedpayroll'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='Prevailing Wage' name='prevailingwage'/>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label='CCIP Insurance' name='ccipinsurance'/>
            <Divider>Third-Party Settings</Divider>
            <CheckboxInput {...props} formItemLayout={formItemLayout} label={<span>
                <TooltipIcon tooltipProps={{ className: 'color-primary-2' }} type='question-circle'
                             title='Users invited to bid can view contacts'/>{' '}Third-Party Contacts
              </span>} name='thirdpartycontacts'/>

            <Form.Item className='submit-controls' {...tailFormItemLayout}>
              <Button.Group compact>
                <Button onClick={handleSubmit} type='primary'>Save</Button>
                <Button onClick={handleReset}>Clear</Button>
                <Button onClick={onCancel}>Cancel</Button>
              </Button.Group>
            </Form.Item>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BidForm;



// WEBPACK FOOTER //
// ./src/components/Bids/BidForm.js